<template>
  <!-- Loading status indicator -->
  <div v-if="store.indicateLoading" class="fixed top-0 left-0 w-screen h-[2px] bg-green-500 opacity-95 z-50 animate-pulse"></div>
  <div v-if="store.indicateError" class="fixed top-0 left-0 w-screen h-[2px] bg-red-500 opacity-95 z-50"></div>
  <!-- Alert Notifications -->
  <Alert :showAlert="store.alert.showAlert" :isError="store.alert.isError" :alertText="store.alert.alertText"/>
  <!-- App -->
  <div class="flex flex-col h-screen">
    <HeaderView :selectedYear="$route.params.year" :selectedMonth="$route.params.month" :selectedSSP=$route.params.ssp />
    <div v-if="!loginComplete" class="flex flex-row h-96 justify-center items-center text-black">
      <font-awesome-icon class="animate-spin mr-3 text-black" icon="fa-solid fa-circle-notch" /> Prüfe Berechtigung...
    </div>
    <div v-else-if="isAccessDenied" class="flex flex-row h-96 justify-center items-center">
      <WelcomePane label="Programmatic-Abrechnungsmanager (PAM) - Kein Zugriff">Willkommen im <strong>Programmatic-Abrechnungsmanager</strong>! Dein Unternehmenskonto hat keinen Zugriff auf diese Anwendung!</WelcomePane>
    </div>
    <div v-else-if="isLoggedIn && isInvalidMonth" class="flex flex-row h-96 justify-center items-center">
      <WelcomePane label="Programmatic-Abrechnungsmanager (PAM) - Ungültiger Monat" :isError="true">
        <div>
          Der ausgewählte Monat ist noch nicht verfügbar. Bitte wähle einen Monat bis zum <strong>Vormonat des aktuellen Monats</strong>.
        </div>
        <button
          @click="goToLastAvailableMonth"
          class="mt-4 inline-flex items-center gap-2 rounded-sm bg-white bg-opacity-20 px-5 py-3 text-sm font-medium text-white hover:bg-opacity-30 focus:outline-none transition-all border border-white border-opacity-40"
        >
          <font-awesome-icon icon="fa-chevron-left" />
          <span>Zum letzten verfügbaren Monat</span>
        </button>
      </WelcomePane>
    </div>
    <div v-else-if="isLoggedIn && isInvalidSSP" class="flex flex-row h-96 justify-center items-center">
      <WelcomePane label="Programmatic-Abrechnungsmanager (PAM) - Ungültiger Partner" :isError="true">
        <div>
          Der ausgewählte Partner existiert nicht. Bitte wähle einen <strong>gültigen Partner</strong> aus der Liste.
        </div>
        <button
          @click="goToFirstAvailableSSP"
          class="mt-4 inline-flex items-center gap-2 rounded-sm bg-white bg-opacity-20 px-5 py-3 text-sm font-medium text-white hover:bg-opacity-30 focus:outline-none transition-all border border-white border-opacity-40"
        >
          <font-awesome-icon icon="fa-chevron-left" />
          <span>Zum ersten verfügbaren Partner</span>
        </button>
      </WelcomePane>
    </div>
    <div v-else-if="isLoggedIn" class="">
      <!-- Annual Overview Page -->
      <div v-if="isOverviewPage">
        <AnnualOverview :selectedYear="$route.params.year" />
      </div>
      <!-- Normal SSP Detail View -->
      <div v-else>
        <DetailView :selectedYear="$route.params.year" :selectedMonth="$route.params.month" :selectedSSP="$route.params.ssp"/>
      </div>
    </div>
    <div v-else class="flex flex-row h-96 justify-center items-center">
      <WelcomePane label="Programmatic-Abrechnungsmanager (PAM) - Nicht angemeldet">
        <div>
          Willkommen im <strong>Programmatic-Abrechnungsmanager</strong>! Bitte melde dich mit deinem Unternehmenskonto an, um die Anwendung zu nutzen.
        </div>
        <button
          @click="signIn"
          class="mt-4 inline-flex items-center gap-2 rounded-sm bg-white bg-opacity-20 px-5 py-3 text-sm font-medium text-white hover:bg-opacity-30 focus:outline-none transition-all border border-white border-opacity-40"
        >
          <font-awesome-icon icon="fa-arrow-right-to-bracket" />
          <span>Anmelden</span>
        </button>
      </WelcomePane>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import HeaderView from "@/components/segments/Header.vue";
import WelcomePane from "@/components/reusable/WelcomePane.vue";
import Alert from "@/components/reusable/Alert.vue";
import api from "@/js/api";
import { useMainStore } from '@/stores/main';
import AuthenticationHandler from "@/js/authentication";

// Lazy-load heavy route components
const DetailView = defineAsyncComponent(() => import("@/components/segments/SSPView.vue"));
const AnnualOverview = defineAsyncComponent(() => import("@/components/segments/AnnualOverview.vue"));

export default {
  name: 'App',
  components: {Alert, AnnualOverview, WelcomePane, DetailView, HeaderView},
  data() {
    return {
      isLoggedIn: false,
      isAccessDenied: false,
      loginComplete: false
    };
  },
  computed: {
    store() {
      return useMainStore();
    },
    isOverviewPage() {
      return this.$route.path.startsWith('/overview/');
    },
    isInvalidMonth() {
      // Only validate on detail view pages (not overview)
      if (this.isOverviewPage) {
        return false;
      }

      const year = parseInt(this.$route.params.year);
      const month = parseInt(this.$route.params.month);

      // Get current date
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

      // Check if the requested month is in the future
      if (year > currentYear) {
        return true;
      }

      if (year === currentYear && month > currentMonth) {
        return true;
      }

      return false;
    },
    isInvalidSSP() {
      // Only validate on detail view pages (not overview)
      if (this.isOverviewPage) {
        return false;
      }

      const sspName = this.$route.params.ssp;

      // Check if SSP name is provided
      if (!sspName) {
        return true;
      }

      // Check if SSP exists in the store
      const allSSPs = this.store.sellingSidePlatforms;
      if (!allSSPs || allSSPs.length === 0) {
        // Store not yet loaded, don't show error
        return false;
      }

      const sspExists = allSSPs.some(ssp => ssp.ssp_name === sspName);
      return !sspExists;
    }
  },
  created() {
    // Watch route change to set page title
    this.$watch(
      () => [this.$route.params.year, this.$route.params.month, this.$route.path], () => {
        if (this.isOverviewPage) {
          window.document.title = "PAM: Programmatic Abrechnungsmanager - Jahresübersicht " + this.$route.params.year
        } else if (this.$route.params.year && this.$route.params.month) {
          window.document.title = "PAM: Programmatic Abrechnungsmanager - " + this.$route.params.year + "-" + this.$route.params.month.toString().padStart(2, "0")
        }
      }
    )

    // Set initial page title
    if (this.isOverviewPage) {
      window.document.title = "PAM: Programmatic Abrechnungsmanager - Jahresübersicht " + this.$route.params.year
    } else if (this.$route.params.year && this.$route.params.month) {
      window.document.title = "PAM: Programmatic Abrechnungsmanager - " + this.$route.params.year + "-" + this.$route.params.month.toString().padStart(2, "0")
    }

    this.$emitter.on('login', (account) => {
      this.isLoggedIn = true;
    })
    this.$emitter.on('logout', () => {
      this.isLoggedIn = false;
    });
    this.$emitter.on('accessDenied', () => {
      this.isAccessDenied = true;
    })
    this.$emitter.on('ssoLoadComplete', () => {
      this.loginComplete = true;
    })
  },
  methods: {
    goToLastAvailableMonth() {
      // Get current date
      const now = new Date();

      // Calculate last month
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const targetYear = lastMonthDate.getFullYear();
      const targetMonth = lastMonthDate.getMonth() + 1; // JavaScript months are 0-indexed

      // Get all available SSPs (not filtered by month)
      const allSSPs = this.store.sellingSidePlatforms;

      // If we have SSPs, get the first one alphabetically
      let firstSSP;
      if (allSSPs && allSSPs.length > 0) {
        const sortedSSPs = [...allSSPs].sort((a, b) => a.ssp_name.localeCompare(b.ssp_name));
        firstSSP = sortedSSPs[0].ssp_name;
      } else {
        // Fallback to a default
        firstSSP = "AdDefend";
      }

      // Navigate to the last available month
      this.$router.push("/" + targetYear + "/" + targetMonth + "/" + encodeURIComponent(firstSSP));
    },
    signIn() {
      AuthenticationHandler.signIn();
    },
    goToFirstAvailableSSP() {
      // Get current year and month from route
      const year = this.$route.params.year;
      const month = this.$route.params.month;

      // Get all available SSPs (not filtered by month)
      const allSSPs = this.store.sellingSidePlatforms;

      // If we have SSPs, get the first one alphabetically
      let firstSSP;
      if (allSSPs && allSSPs.length > 0) {
        const sortedSSPs = [...allSSPs].sort((a, b) => a.ssp_name.localeCompare(b.ssp_name));
        firstSSP = sortedSSPs[0].ssp_name;
      } else {
        // Fallback to a default
        firstSSP = "AdDefend";
      }

      // Navigate to the same year/month with first SSP
      this.$router.push("/" + year + "/" + month + "/" + encodeURIComponent(firstSSP));
    }
  }
}
</script>

<style>
body {
  background-color: rgb(156 163 175 / 0.25);
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  text-align: center;
}
</style>
